import { useState, useEffect, lazy, Suspense } from 'react';
import { Box, VStack, useToast, Spinner, Center, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, Button, FormControl, FormLabel, Input, useDisclosure } from '@chakra-ui/react';
import Cookies from 'js-cookie';
import { getTranslations } from './translations';
import { debugLog } from './utils/debug';
import { v4 as uuidv4 } from 'uuid'; // We need IDs for reordering

// Components - Core components loaded immediately
import Header from './components/Header';
import TimerDisplay from './components/TimerDisplay';
import ConfigForm from './components/ConfigForm';
import Controls from './components/Controls';
import ShareButtons from './components/ShareButtons';
import Footer from './components/Footer';
import IconSequencer from './components/IconSequencer'; // Load directly to ensure framer-motion loads with React

// Lazy loaded components - loaded only when needed
const IconGallery = lazy(() => import('./components/IconGallery'));
const IconDownloader = lazy(() => import('./components/IconDownloader'));

// Hooks
import { useWorkoutTimer } from './hooks/useWorkoutTimer';
import { useScreenFeatures } from './hooks/useScreenFeatures';
import { getLocalizedIcon, getIconById } from './data/iconRepository';

const defaultDownloadedIconIds = ['lift-1', 'lat-pulldown-1', 'biceps-standing-1'];

function App() {
  debugLog('App', 'render', 'Component rendering');

  const t = getTranslations();
  const toast = useToast();

  // Navigation State
  const [currentView, setCurrentView] = useState('timer'); // 'timer', 'gallery', 'download'

  // Icon State
  const [downloadedIconIds, setDownloadedIconIds] = useState(() => {
    const saved = localStorage.getItem('downloadedIcons');
    if (!saved) {
      return defaultDownloadedIconIds;
    }

    try {
      const parsed = JSON.parse(saved);
      const sanitized = Array.isArray(parsed) ? parsed.filter(id => getIconById(id)) : [];
      return sanitized.length > 0 ? sanitized : defaultDownloadedIconIds;
    } catch {
      return defaultDownloadedIconIds;
    }
  });

  useEffect(() => {
    localStorage.setItem('downloadedIcons', JSON.stringify(downloadedIconIds));
  }, [downloadedIconIds]);

  const handleDownloadIcon = (id) => {
    if (!downloadedIconIds.includes(id)) {
      setDownloadedIconIds([...downloadedIconIds, id]);
    }
  };

  const handleRemoveIcon = (id) => {
    setDownloadedIconIds(downloadedIconIds.filter(iconId => iconId !== id));
  };

  // Configuration State
  const [workTime, setWorkTime] = useState(20);
  const [restTime, setRestTime] = useState(10);
  const [rounds, setRounds] = useState(8);
  const [startupTime, setStartupTime] = useState(() => {
    const saved = Cookies.get('startupTime');
    return saved ? parseInt(saved, 10) : 5;
  });
  const [restTimeRules, setRestTimeRules] = useState([]);

  // Sequence State: Array of { uniqueId, iconId }
  const [exercises, setExercises] = useState([]);

  // Current profile name state
  const [currentProfileName, setCurrentProfileName] = useState(null); // null = default, string = nome profilo
  const { isOpen: isSaveModalOpen, onOpen: onSaveModalOpen, onClose: onSaveModalClose } = useDisclosure();
  const [tempProfileName, setTempProfileName] = useState('');

  // Sync exercises with rounds
  useEffect(() => {
    setExercises(prev => {
      if (prev.length === rounds) return prev;

      if (prev.length < rounds) {
        // Add new slots
        const needed = rounds - prev.length;
        const newSlots = Array(needed).fill(null).map(() => ({
          uniqueId: crypto.randomUUID(),
          iconId: null,
          customLabel: ''
        }));
        return [...prev, ...newSlots];
      } else {
        // Trim slots
        return prev.slice(0, rounds);
      }
    });
  }, [rounds]);

  // Saved Configs State
  const [savedConfigs, setSavedConfigs] = useState(() => {
    const configs = [];
    for (let i = 1; i <= 4; i++) {
      const config = Cookies.get(`workout-config-${i}`);
      if (config) configs.push(JSON.parse(config));
    }
    return configs;
  });

  // Settings state
  const [wakeLockEnabled, setWakeLockEnabled] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = Cookies.get('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = Cookies.get('soundEnabled');
    return saved ? JSON.parse(saved) : true; // Default: suono attivo
  });

  useEffect(() => {
    Cookies.set('darkMode', JSON.stringify(isDarkMode), { expires: 365 });
  }, [isDarkMode]);

  useEffect(() => {
    Cookies.set('startupTime', startupTime.toString(), { expires: 365 });
  }, [startupTime]);

  useEffect(() => {
    Cookies.set('soundEnabled', JSON.stringify(soundEnabled), { expires: 365 });
  }, [soundEnabled]);

  // Hooks
  const {
    isActive, isPaused, timeLeft, totalTimeLeft, currentPhase, currentRound,
    startTimer: startTimerLogic, togglePause, resetTimer: resetTimerLogic,
    goToNextStep, goToPreviousStep
  } = useWorkoutTimer(workTime, restTime, rounds, startupTime, soundEnabled, restTimeRules);

  // Reset timer quando cambiano i parametri e la sessione non è attiva
  // Questo assicura che quando si carica un profilo, il timer venga resettato
  useEffect(() => {
    if (!isActive && (currentPhase === 'finished' || (currentPhase !== 'ready' && timeLeft > 0))) {
      resetTimerLogic();
    }
  }, [workTime, restTime, rounds, startupTime]);

  // Gestione dinamica AdSense: mostra annunci solo per utenti ricorrenti (con profilo salvato)
  // e rimuove annunci durante la sessione
  useEffect(() => {
    const isSessionActive = isActive || currentPhase === 'countdown' || currentPhase === 'work' || currentPhase === 'rest';
    const hasSavedProfiles = savedConfigs.length > 0; // Solo utenti con personalizzazioni salvate
    
    // Trova o crea lo script AdSense
    let adsScript = document.querySelector('script[src*="adsbygoogle"]');
    
    if (isSessionActive || !hasSavedProfiles) {
      // Rimuovi lo script durante la sessione O se l'utente non ha profili salvati
      if (adsScript) {
        adsScript.remove();
        // Rimuovi anche gli annunci esistenti
        const ads = document.querySelectorAll('.adsbygoogle');
        ads.forEach(ad => ad.remove());
      }
    } else {
      // Carica lo script solo se:
      // 1. Non è già presente
      // 2. La sessione non è attiva
      // 3. L'utente ha almeno un profilo salvato (utente ricorrente)
      if (!adsScript && hasSavedProfiles) {
        adsScript = document.createElement('script');
        adsScript.async = true;
        adsScript.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1094529293967820';
        adsScript.crossOrigin = 'anonymous';
        document.head.appendChild(adsScript);
      }
    }
  }, [isActive, currentPhase, savedConfigs]);

  const { requestWakeLock, releaseWakeLock, requestFullscreen, exitFullscreen, isFullscreen } = useScreenFeatures();

  // Handlers
  const handleStart = async () => {
    // Scroll to absolute top - multiple methods for reliability
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    if (wakeLockEnabled) {
      await requestWakeLock();
    }
    startTimerLogic();
  };

  const handleReset = () => {
    resetTimerLogic();
    releaseWakeLock();
    exitFullscreen();
    // Non resettare currentProfileName qui - rimane quello del profilo caricato
  };

  const handleSaveClick = () => {
    setTempProfileName(currentProfileName || '');
    onSaveModalOpen();
  };

  const saveCurrentConfig = () => {
    const profileName = tempProfileName.trim() || null;
    const newConfig = {
      workTime,
      restTime,
      rounds,
      startupTime,
      exercises: exercises.map(e => ({ iconId: e.iconId, customLabel: e.customLabel || '' })),
      timestamp: new Date().toLocaleString(),
      name: profileName, // Aggiungi il nome al config
      restTimeRules: restTimeRules && Array.isArray(restTimeRules) && restTimeRules.length > 0 ? restTimeRules : undefined
    };

    const updatedConfigs = [newConfig, ...savedConfigs.slice(0, 3)];
    setSavedConfigs(updatedConfigs);

    updatedConfigs.forEach((config, index) => {
      Cookies.set(`workout-config-${index + 1}`, JSON.stringify(config), { expires: 365 });
    });

    // Imposta il nome del profilo corrente
    setCurrentProfileName(profileName);

    onSaveModalClose();
    toast({
      title: t.configSaved,
      status: 'success',
      duration: 2000,
      isClosable: true,
      position: 'top'
    });
  };

  const deleteConfig = (indexToDelete) => {
    const updatedConfigs = savedConfigs.filter((_, index) => index !== indexToDelete);
    setSavedConfigs(updatedConfigs);

    // Re-sync cookies
    // 1. Clear all old cookies
    for (let i = 1; i <= 4; i++) {
      Cookies.remove(`workout-config-${i}`);
    }
    // 2. Set new cookies
    updatedConfigs.forEach((config, index) => {
      Cookies.set(`workout-config-${index + 1}`, JSON.stringify(config), { expires: 365 });
    });

    toast({
      title: "Profilo eliminato",
      status: 'warning',
      duration: 2000,
      isClosable: true,
      position: 'top'
    });
  };

  // Styles
  const getBackgroundColor = () => {
    if (isDarkMode) {
      if (currentView !== 'timer') return 'linear(to-br, gray.800, gray.900)';
      switch (currentPhase) {
        case 'countdown': return 'linear(to-br, gray.800, gray.700)';
        case 'work': return 'linear(to-br, gray.700, gray.800)';
        case 'rest': return 'linear(to-br, gray.800, purple.900)';
        default: return 'linear(to-br, gray.800, gray.700)';
      }
    } else {
      if (currentView !== 'timer') return 'linear(to-br, gray.100, gray.300)';
      switch (currentPhase) {
        case 'countdown': return 'linear(to-br, brand.200, brand.400)';
        case 'work': return 'linear(to-br, brand.400, brand.600)';
        case 'rest': return 'linear(to-br, brand.600, purple.400)';
        default: return 'linear(to-br, brand.300, brand.500)';
      }
    }
  };

  const getBackgroundAnimation = () => ({
    transition: 'all 0.3s ease',
  });

  // Watch for finish to cleanup screen lock
  useEffect(() => {
    if (currentPhase === 'finished') {
      releaseWakeLock();
      exitFullscreen();
    }
  }, [currentPhase, releaseWakeLock, exitFullscreen]);

  // Determine current icon and label
  const currentExercise = exercises[currentRound - 1];
  const currentIconId = currentExercise?.iconId;
  const currentLabel = currentExercise?.customLabel;

  return (
    <Box
      minHeight="100vh"
      bgGradient={getBackgroundColor()}
      p={4}
      sx={getBackgroundAnimation()}
      position="relative"
    >
      <VStack
        spacing={4}
        align="center"
        maxW="600px"
        mx="auto"
        w="100%"
        pt={currentView === 'timer' ? 8 : 12}
        pb={24}
        bgGradient={{ base: 'none', md: isDarkMode ? 'linear(to-b, gray.800, gray.900)' : 'linear(to-b, teal.50, blue.50)' }}
        borderRadius={{ base: 'none', md: 'xl' }}
        boxShadow={{ base: 'none', md: 'xl' }}
        border={{ base: 'none', md: '1px solid' }}
        borderColor={{ base: 'transparent', md: isDarkMode ? 'gray.700' : 'gray.200' }}
        px={{ base: 0, md: 6 }}
        position="relative"
      >
        {/* Cornice interna per contrasto visivo */}
        <Box
          position="absolute"
          top={{ base: 0, md: '5px' }}
          left={{ base: 0, md: '5px' }}
          right={{ base: 0, md: '5px' }}
          bottom={{ base: 0, md: '5px' }}
          border={{ base: 'none', md: '3px solid' }}
          borderColor={{ base: 'transparent', md: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)' }}
          borderRadius={{ base: 'none', md: 'xl' }}
          pointerEvents="none"
          zIndex={0}
        />
        <Header
          onNavigate={setCurrentView}
          wakeLockEnabled={wakeLockEnabled}
          setWakeLockEnabled={setWakeLockEnabled}
          isDarkMode={isDarkMode}
          setIsDarkMode={setIsDarkMode}
          startupTime={startupTime}
          setStartupTime={setStartupTime}
          soundEnabled={soundEnabled}
          setSoundEnabled={setSoundEnabled}
          isFullscreen={isFullscreen}
        />

        {currentView === 'timer' && (
          <>
            {!isActive && (
              <ConfigForm
                workTime={workTime} setWorkTime={setWorkTime}
                restTime={restTime} setRestTime={setRestTime}
                rounds={rounds} setRounds={setRounds}
                startupTime={startupTime} setStartupTime={setStartupTime}
                isActive={isActive}
                savedConfigs={savedConfigs} setSavedConfigs={setSavedConfigs}
                setExercises={setExercises}
                onDeleteConfig={deleteConfig}
                isDarkMode={isDarkMode}
                setCurrentProfileName={setCurrentProfileName}
                resetTimer={handleReset}
                restTimeRules={restTimeRules}
                setRestTimeRules={setRestTimeRules}
              />
            )}

            <TimerDisplay
              timeLeft={timeLeft}
              totalTimeLeft={totalTimeLeft}
              workTime={workTime}
              restTime={restTime}
              rounds={rounds}
              currentPhase={currentPhase}
              startupTime={startupTime}
              currentIconId={currentIconId}
              currentLabel={currentLabel}
              exercises={exercises}
              isDarkMode={isDarkMode}
              isActive={isActive}
              isPaused={isPaused}
              currentRound={currentRound}
              goToNextStep={goToNextStep}
              goToPreviousStep={goToPreviousStep}
              startTimer={handleStart}
              profileName={currentProfileName}
              hasSavedConfigs={savedConfigs.length > 0}
              togglePause={togglePause}
              resetTimer={handleReset}
              requestFullscreen={requestFullscreen}
              exitFullscreen={exitFullscreen}
              isFullscreen={isFullscreen}
              restTimeRules={restTimeRules}
            />

            {!isActive && (
              <IconSequencer
                exercises={exercises}
                setExercises={setExercises}
                availableIcons={downloadedIconIds.map(id => getLocalizedIcon(getIconById(id))).filter(Boolean)}
                isDarkMode={isDarkMode}
              />
            )}

            <Controls
              isActive={isActive}
              isPaused={isPaused}
              startTimer={handleStart}
              togglePause={togglePause}
              resetTimer={handleReset}
              saveCurrentConfig={handleSaveClick}
              isDarkMode={isDarkMode}
            />
            <ShareButtons isDarkMode={isDarkMode} />
            <Footer isDarkMode={isDarkMode} />
          </>
        )}

        {currentView === 'gallery' && (
          <Suspense fallback={
            <Center py={8}>
              <Spinner size="lg" color="teal.500" />
            </Center>
          }>
            <IconGallery
              downloadedIconIds={downloadedIconIds}
              onRemove={handleRemoveIcon}
              isDarkMode={isDarkMode}
            />
          </Suspense>
        )}

        {currentView === 'download' && (
          <Suspense fallback={
            <Center py={8}>
              <Spinner size="lg" color="teal.500" />
            </Center>
          }>
            <IconDownloader
              downloadedIconIds={downloadedIconIds}
              onDownload={handleDownloadIcon}
              isDarkMode={isDarkMode}
            />
          </Suspense>
        )}

        {/* Modal per salvare con nome */}
        <Modal isOpen={isSaveModalOpen} onClose={onSaveModalClose} isCentered>
          <ModalOverlay />
          <ModalContent bg={isDarkMode ? 'gray.800' : 'white'} borderColor={isDarkMode ? 'gray.700' : 'gray.200'}>
            <ModalHeader color={isDarkMode ? 'white' : 'gray.800'}>
              {t.saveProfileNameTitle || 'Salva Profilo'}
            </ModalHeader>
            <ModalCloseButton color={isDarkMode ? 'white' : 'gray.800'} />
            <ModalBody>
              <FormControl>
                <FormLabel color={isDarkMode ? 'gray.200' : 'gray.700'} mb={3}>
                  {t.saveProfileNameLabel || 'Nome profilo (opzionale)'}
                </FormLabel>
                <Input
                  type="text"
                  value={tempProfileName}
                  onChange={(e) => setTempProfileName(e.target.value)}
                  placeholder={t.saveProfileNamePlaceholder || 'Es: Tabata, HIIT, Cardio...'}
                  bg={isDarkMode ? 'gray.700' : 'white'}
                  color={isDarkMode ? 'white' : 'gray.800'}
                  borderColor={isDarkMode ? 'gray.600' : 'gray.300'}
                  _focus={{ borderColor: 'teal.500', boxShadow: '0 0 0 1px teal.500' }}
                  maxLength={30}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      saveCurrentConfig();
                    }
                  }}
                />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="ghost"
                mr={3}
                onClick={onSaveModalClose}
                color={isDarkMode ? 'gray.300' : 'gray.600'}
              >
                {t.startupTimeDialogCancel || 'Annulla'}
              </Button>
              <Button colorScheme="teal" onClick={saveCurrentConfig}>
                {t.startupTimeDialogSave || 'Salva'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

      </VStack>
    </Box>
  );
}

export default App;